'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type { TooltipProps } from 'recharts';

const ResponsiveContainer = dynamic(() => import('recharts').then((m) => m.ResponsiveContainer), { ssr: false });
const BarChart = dynamic(() => import('recharts').then((m) => m.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then((m) => m.Bar), { ssr: false });
const XAxis = dynamic(() => import('recharts').then((m) => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then((m) => m.YAxis), { ssr: false });
const CartesianGrid = dynamic(() => import('recharts').then((m) => m.CartesianGrid), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then((m) => m.Tooltip), { ssr: false });
const Cell = dynamic(() => import('recharts').then((m) => m.Cell), { ssr: false });

const distributionData = [
  { bucket: '<-$500', count: 4, type: 'loss' },
  { bucket: '-$500–0', count: 30, type: 'loss' },
  { bucket: '$0–$500', count: 22, type: 'win' },
  { bucket: '$500–$1k', count: 18, type: 'win' },
  { bucket: '$1k–$2k', count: 10, type: 'win' },
  { bucket: '>$2k', count: 4, type: 'win' },
];

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-600 text-foreground">{payload[0]?.value} trades</p>
    </div>
  );
}

export default function BacktestTradeDistribution() {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="mb-4">
        <h4 className="text-sm font-600 text-foreground">Trade P&L Distribution</h4>
        <p className="text-xs text-muted-foreground mt-0.5">Frequency of trade outcomes by P&L bucket</p>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={distributionData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="bucket"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {distributionData.map((entry) => (
                <Cell
                  key={`dist-${entry.bucket}`}
                  fill={entry.type === 'win' ? 'var(--primary)' : 'var(--negative)'}
                  fillOpacity={0.8}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary stats below chart */}
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
        {[
          { label: 'Avg Win', value: '+$793', color: 'text-positive' },
          { label: 'Avg Loss', value: '-$578', color: 'text-negative' },
          { label: 'Expectancy', value: '+$214', color: 'text-positive' },
        ].map((stat) => (
          <div key={`dist-stat-${stat.label}`} className="text-center">
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className={`text-sm font-600 font-mono-nums mt-0.5 ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}